// After a deploy, a tab opened on the previous build asks for chunk files that no longer exist,
// and the host's SPA fallback answers with index.html ("disallowed MIME type text/html"). Reload
// once onto the page being opened so the tab picks up the new build. The timestamp guard stops a
// reload loop when the chunk is missing for another reason.
const KEY = 'stale-chunk-reload';
const WINDOW_MS = 10_000;
let prefetching = 0; // prefetchRoutes loads in flight
const failedPrefetches = new Set(); // route records whose prefetch failed

const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|disallowed MIME type|Unable to preload CSS/i;

export const isStaleChunkError = (error) => CHUNK_ERROR.test(String(error?.message || error || ''));

export function reloadForStaleChunk(path, {
  storage = window.sessionStorage, location = window.location,
} = {}) {
  let last = 0;
  try { last = Number(storage.getItem(KEY)) || 0; } catch { /* storage blocked: still try once */ }
  if (Date.now() - last < WINDOW_MS) return false;
  try { storage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
  if (path && path !== location.pathname + location.search + location.hash) location.assign(path);
  else location.reload();
  return true;
}

export function installStaleChunkReload(router, target = window) {
  // Vite's preload error fires before router.onError, so remember where a navigation is going:
  // both paths then reload onto that page, not the one being left.
  let pending = null;
  router.beforeEach((to) => {
    pending = to.fullPath;
    // Vite never retries a stylesheet whose preload failed, so a page whose prefetch failed
    // could render without its CSS on a client-side navigation: load that page fresh instead
    const failed = to.matched.some((r) => failedPrefetches.has(r));
    if (failed && reloadForStaleChunk(to.fullPath)) return false;
    return true;
  });
  router.afterEach(() => { pending = null; });
  router.onError((error, to) => {
    const path = to?.fullPath || pending;
    pending = null; // afterEach does not run for a rejected navigation
    if (isStaleChunkError(error)) reloadForStaleChunk(path);
  });
  target.addEventListener('vite:preloadError', (event) => {
    // A failed prefetch outside a navigation does not reload now; navigating to that page does
    if (!pending && prefetching) return;
    if (reloadForStaleChunk(pending)) event.preventDefault();
  });
}

// Downloads the chunks of lazy route records ahead of a click, so the navigation does not wait
// on the network. A failure only makes a later navigation to that route a full page load.
export function prefetchRoutes(records) {
  records.forEach((r) => {
    const load = r.components?.default;
    if (typeof load !== 'function') return; // eager, or already loaded by the router
    prefetching += 1;
    Promise.resolve().then(load)
      .catch(() => { failedPrefetches.add(r); })
      .finally(() => { prefetching -= 1; });
  });
}
